import React, { useEffect, useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../actions";
import OuterDraggable from "react-draggable";
import ReactSelect from "react-select";
import Loader from "../../../common/components/Loader";
import { RECOMMENDATION_CRITICALITY_TYPES } from "../../constants";
import Portal from "../../../common/components/Portal";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import { isEqual } from "lodash";

export const CAColumnConfigModal = ({ isView = false, isSkeletonView = false, ...props }) => {
    const dispatch = useDispatch();

    const { caColumns, exportTemplateList, exportPropertyList } = useSelector(state => state.smartChartReducer);
    const [isLoading, setLoading] = useState(true);
    const [bands, setBands] = useState([]);
    const [search, setSearch] = useState("");
    const [allSelected, setAllSelected] = useState(false);
    const [transform, setTransform] = useState({ left: 0, top: 0 });
    const [template, setTemplate] = useState("");
    const [property, setProperty] = useState("");
    const [showErrorBorder, setShowErrorBorder] = useState("");
    const [hoveredItemId, setHoveredItemId] = useState(null);
    const [tableConfig, setTableConfig] = useState({ ca_chart_type_table_heading: { label: "" }, show_config: { show_table_heading: true } });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [initialValues, setInitialValues] = useState({ bands: {}, tableConfig: {}, template: "", property: "" });
    const [expandedFields, setExpandedFields] = React.useState([]);

    useEffect(() => {
        fetchColumns();
        dispatch(
            actions.getExportTemplateListForSmartChartRecom({
                project_id: props?.selectedProject,
                client_id: props?.selectedClient
            })
        );
        dispatch(
            actions.getExportPropertyListForSmartChartRecom({
                project_id: props?.selectedProject,
                client_id: props?.selectedClient
            })
        );
    }, []);

    useEffect(() => {
        setStructuredColumns();
    }, [caColumns, props.selectedFields]);

    useEffect(() => {
        const getExportOptions = exportData => {
            const local = exportData?.with_client?.map(elem => ({ value: elem.id, label: elem.name })) || [];
            const master = exportData?.without_client?.map(elem => ({ value: elem.id, label: elem.name })) || [];
            return [
                { label: "From Local", options: local },
                { label: "From Master", options: master }
            ];
        };

        const exportPropertiesOptions = getExportOptions(exportPropertyList || {});
        const exportTemplatesOptions = getExportOptions(exportTemplateList || {});

        const findSelectedValue = (list, selectedId) => list.find(elem => elem?.value === selectedId);

        const selectedProperty = findSelectedValue(
            [...exportPropertiesOptions[0].options, ...exportPropertiesOptions[1].options],
            caColumns?.list?.property_id
        );
        const selectedTemplate = findSelectedValue(
            [...exportTemplatesOptions[0].options, ...exportTemplatesOptions[1].options],
            caColumns?.list?.template_id
        );

        setInitialValues(prevState => ({
            ...prevState,
            property: props?.selectedProperty
                ? findSelectedValue([...exportPropertiesOptions[0].options, ...exportPropertiesOptions[1].options], props.selectedProperty)
                : selectedProperty,
            template: props?.selectedTemplate
                ? findSelectedValue([...exportTemplatesOptions[0].options, ...exportTemplatesOptions[1].options], props.selectedTemplate)
                : selectedTemplate
        }));

        setProperty(
            props?.selectedProperty
                ? findSelectedValue([...exportPropertiesOptions[0].options, ...exportPropertiesOptions[1].options], props.selectedProperty)
                : selectedProperty
        );
        setTemplate(
            props?.selectedTemplate
                ? findSelectedValue([...exportTemplatesOptions[0].options, ...exportTemplatesOptions[1].options], props.selectedTemplate)
                : selectedTemplate
        );
    }, [exportPropertyList, exportTemplateList, caColumns, props.selectedProperty, props.selectedTemplate]);

    const filteredBands = useMemo(() => {
        if (!search?.trim()) return bands;
        const q = search?.toLowerCase();
        return bands.map(band => ({
            ...band,
            items: band.items.filter(item => item.name?.toLowerCase()?.includes(q))
        }));
    }, [bands, search]);

    useEffect(() => {
        const visibleItems = filteredBands.flatMap(band => band.items);
        if (visibleItems.length === 0) {
            setAllSelected(false);
        } else {
            const allVisSelected = visibleItems.every(item => item.selected);
            setAllSelected(allVisSelected);
        }
    }, [filteredBands]);

    const setStructuredColumns = () => {
        const { property_id, template_id, table_config, ...columns } = caColumns?.list ?? {};

        let backendColumns = {};
        Object.entries(columns).forEach(([bandKey, bandData]) => {
            let parsedFields = {};
            try {
                parsedFields = typeof bandData.fields === "string" ? JSON.parse(bandData.fields) : bandData.fields ?? {};
            } catch {
                parsedFields = {};
            }
            backendColumns[bandKey] = {
                config: bandData.config ?? {},
                fields: parsedFields
            };
        });

        let savedColumns = {};
        if (props.selectedFields && Object.keys(props.selectedFields).length > 0) {
            try {
                savedColumns = typeof props.selectedFields === "string" ? JSON.parse(props.selectedFields) : props.selectedFields;
            } catch {
                savedColumns = {};
            }
        }

        const hasSavedBands = Object.keys(savedColumns).length > 0;
        const bandOrder = hasSavedBands
            ? [...Object.keys(savedColumns), ...Object.keys(backendColumns).filter(k => !savedColumns[k])]
            : Object.keys(backendColumns);

        const newBands = bandOrder.map(key => {
            const backendBand = backendColumns[key] || {};
            const savedBand = savedColumns[key] || {};
            const backendFields = backendBand.fields || {};
            const savedFields = savedBand.fields || {};

            const hasSavedFields = Object.keys(savedFields).length > 0;
            const columnOrder = hasSavedFields
                ? [...Object.keys(savedFields), ...Object.keys(backendFields).filter(k => !savedFields[k])]
                : Object.keys(backendFields);

            const items = columnOrder
                .filter(colKey => backendFields[colKey])
                .map(colKey => {
                    const backendCol = backendFields[colKey];
                    const savedCol = savedFields[colKey] || {};
                    const originalLabel = backendCol.label;
                    const userSetLabel = savedCol.user_set_label ?? backendCol.user_set_label;
                    const displayName = userSetLabel || originalLabel;

                    return {
                        id: colKey,
                        originalLabel,
                        name: displayName,
                        width: savedCol.width ?? backendCol.width,
                        heading_alignment: savedCol.heading_alignment ?? backendCol.heading_alignment,
                        body_alignment: savedCol.body_alignment ?? backendCol.body_alignment,
                        selected: savedCol.selected ?? backendCol.selected,
                        show_total: savedCol.show_total ?? backendCol.show_total,
                        data_type: backendCol.data_type,
                        show_dollar: savedCol.show_dollar ?? backendCol.show_dollar
                    };
                });

            const label = backendBand.config?.label ? backendBand.config.label : key.charAt(0).toUpperCase() + key.slice(1);

            const originalTotalLabel = backendBand.config?.total_label ?? "";
            const userSetTotalLabel = savedBand.config?.user_set_label ?? backendBand.config?.user_set_label;
            const displayTotalLabel = userSetTotalLabel || originalTotalLabel;

            return {
                key,
                label,
                config: {
                    ...backendBand.config,
                    ...savedBand.config,
                    displayTotalLabel,
                    originalTotalLabel
                },
                items
            };
        });

        let latestTableConfig = {};
        if (props?.tableConfig && Object.keys(props.tableConfig).length > 0) {
            latestTableConfig = props.tableConfig;
            setTableConfig(props.tableConfig);
        } else {
            latestTableConfig = table_config;
        }
        setBands(newBands);
        setTableConfig(latestTableConfig);
        setInitialValues({ ...initialValues, bands: [...newBands], tableConfig: { ...latestTableConfig } });
    };

    const handleSelectAll = () => {
        const visibleItemIds = new Set(filteredBands.flatMap(band => band.items.map(item => item.id)));

        const allVisibleSelected = filteredBands.flatMap(band => band.items).every(item => item.selected);

        setBands(prevBands =>
            prevBands.map(band => ({
                ...band,
                items: band.items.map(item => (visibleItemIds.has(item.id) ? { ...item, selected: !allVisibleSelected } : item))
            }))
        );

        setAllSelected(!allVisibleSelected);
    };

    const handleItemCheck = (bandKey, itemId) => {
        setBands(prevBands =>
            prevBands.map(band =>
                band.key === bandKey
                    ? {
                          ...band,
                          items: band.items.map(item => (item.id === itemId ? { ...item, selected: !item.selected } : item))
                      }
                    : band
            )
        );
    };
    const handleBandSelectAll = bandKey => {
        const searchLower = search?.toLowerCase()?.trim();

        setBands(prevBands =>
            prevBands.map(band => {
                if (band.key === bandKey) {
                    const visibleItems = band.items.filter(item => !searchLower || item.name?.toLowerCase().includes(searchLower));
                    const allVisibleSelected = visibleItems.every(item => item.selected);

                    return {
                        ...band,
                        items: band.items.map(item => {
                            const isVisible = !searchLower || item.name?.toLowerCase().includes(searchLower);
                            return isVisible ? { ...item, selected: !allVisibleSelected } : item;
                        })
                    };
                }
                return band;
            })
        );
    };

    const onDragEnd = result => {
        const { source, destination, type } = result;
        if (!destination) return;
        if (type === "CATEGORY") {
            const newBands = Array.from(bands);
            const [movedBand] = newBands.splice(source.index, 1);
            newBands.splice(destination.index, 0, movedBand);
            setBands(newBands);
        } else {
            const sourceBandIndex = parseInt(source.droppableId.split("-")[1], 10);
            const destBandIndex = parseInt(destination.droppableId.split("-")[1], 10);
            if (sourceBandIndex === destBandIndex) {
                const newItems = Array.from(bands[sourceBandIndex].items);
                const [movedItem] = newItems.splice(source.index, 1);
                newItems.splice(destination.index, 0, movedItem);
                const newBands = Array.from(bands);
                newBands[sourceBandIndex] = { ...newBands[sourceBandIndex], items: newItems };
                setBands(newBands);
            } else {
                const sourceItems = Array.from(bands[sourceBandIndex].items);
                const [movedItem] = sourceItems.splice(source.index, 1);
                const destItems = Array.from(bands[destBandIndex].items);
                destItems.splice(destination.index, 0, movedItem);
                const newBands = Array.from(bands);
                newBands[sourceBandIndex] = { ...newBands[sourceBandIndex], items: sourceItems };
                newBands[destBandIndex] = { ...newBands[destBandIndex], items: destItems };
                setBands(newBands);
            }
        }
    };

    const handleDrag = (e, ui) => {
        const { x, y } = ui;
        setTransform({ left: x, top: y });
    };

    const handleSelectExportProperty = value => {
        setProperty(value);
    };

    const fetchColumns = selectedProperty => {
        const { line, column } = props.selectedElements;
        const { isSwitchBandActive } = props;
        const formattedLine = Array.isArray(line) ? line.join("_") : line;
        const formattedColumn = Array.isArray(column) ? column.join("_") : column;
        const columnKey = isSwitchBandActive ? `${formattedColumn}_${formattedLine}` : `${formattedLine}_${formattedColumn}`;
        let params = {
            keyword: columnKey,
            client_id: props?.selectedClient || "",
            project_id: props?.selectedProject || "",
            comb_fields: props.filter,
            reverse_cmp_analysis: isSwitchBandActive
        };
        if (column[0] === "recomtype") {
            params.comb_fields = RECOMMENDATION_CRITICALITY_TYPES;
            params.is_legacy = props.isLegacyExport;
        }
        if (selectedProperty) {
            params = { ...params, property_id: selectedProperty };
        }
        const result = dispatch(actions.getCaColumns(params, "multi_combination"));
        if (result && typeof result.then === "function") {
            result.then(() => setLoading(false)).catch(() => setLoading(false));
        } else {
            setLoading(false);
        }
    };

    const handleSave = () => {
        if (!property?.value?.trim()?.length || !template?.value?.trim()?.length) {
            setShowErrorBorder("true");
            return;
        }
        const transformedBands = transformBandsToBackendFormat(bands);
        props.onSave({
            selectedColumnKeys: transformedBands,
            property: property?.value,
            template: template?.value,
            selectedTableConfig: tableConfig
        });
    };

    const transformBandsToBackendFormat = bands => {
        return bands.reduce((acc, band) => {
            const columnsObj = {};
            band.items.forEach(item => {
                const userSet = item.name !== item.originalLabel ? item.name : null;
                columnsObj[item.id] = {
                    label: item.originalLabel,
                    user_set_label: userSet,
                    width: item.width,
                    heading_alignment: item.heading_alignment,
                    body_alignment: item.body_alignment,
                    selected: item.selected,
                    show_total: item.show_total,
                    data_type: item.data_type,
                    show_dollar: item.show_dollar,
                    group: band.key
                };
            });

            const userSetBandTotalLabel = band.config.originalTotalLabel !== band.config.displayTotalLabel ? band.config.displayTotalLabel : null;
            acc[band.key] = {
                config: {
                    show_total: band.config.show_total,
                    label: band.config.label,
                    total_label: band.config.total_label,
                    user_set_label: userSetBandTotalLabel,
                    width: band.config.width,
                    heading_alignment: band.config.heading_alignment,
                    body_alignment: band.config.body_alignment
                },
                fields: columnsObj
            };

            return acc;
        }, {});
    };

    const handleCancel = () => {
        if (
            isEqual(initialValues.bands, bands) &&
            isEqual(initialValues.tableConfig, tableConfig) &&
            isEqual(initialValues.template, template) &&
            isEqual(initialValues.property, property)
        ) {
            props.onCancel();
        } else {
            setShowConfirmModal(true);
        }
    };

    const renderConfirmationModal = () => {
        if (!showConfirmModal) return null;

        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading="Do you want to cancel and lose all changes?"
                        type="cancel"
                        message="This action cannot be reverted, are you sure you want to cancel?"
                        onNo={() => setShowConfirmModal(false)}
                        onYes={() => {
                            setShowConfirmModal(false);
                            props.onCancel();
                        }}
                    />
                }
                onCancel={() => setShowConfirmModal(false)}
            />
        );
    };
    const toggleCollapse = id => {
        setExpandedFields(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
    };

    const handleAlignmentChange = (key, value, bandIndex, itemIndex) => {
        const updated = [...bands];
        updated[bandIndex].items[itemIndex][key] = value;
        setBands(updated);
    };

    return (
        <React.Fragment>
            <div className="modal modal-region modal-view reco-view-mdl column-setting-modal" id="modalId" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog" role="document">
                    <OuterDraggable handle=".draggable" cancel=".not-draggable" onDrag={handleDrag}>
                        <div className="modal-content outerModal_draggable" style={transform}>
                            <div className="modal-header draggable d-flex justify-content-between align-items-center">
                                <h5 className="modal-title" id="exampleModalLabel">
                                    <div className="txt-hed">CA Column Settings</div>
                                </h5>
                                <div className="d-flex align-items-center">
                                    {props.hasStepper && <span className="step-indicator mr-2">Step 2/2</span>}
                                    <button type="button" className="close not-draggable" onClick={handleCancel}>
                                        <span aria-hidden="true">
                                            <img src="/img/close.svg" alt="" />
                                        </span>
                                    </button>
                                </div>
                            </div>
                            <div className="modal-body region-otr">
                                {/* Global Controls for search and export selects */}
                                <div className="global-controls mb-3">
                                    <div className="d-flex align-items-end mb-2">
                                        <div className="flex-grow-1">
                                            <div className="position-relative">
                                                <i
                                                    className="fas fa-search position-absolute"
                                                    style={{
                                                        left: "10px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        color: "#aaa"
                                                    }}
                                                ></i>
                                                <input
                                                    type="text"
                                                    className="form-control pl-5"
                                                    placeholder="Search Columns"
                                                    value={search}
                                                    onChange={e => setSearch(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="ml-3 mb-1">
                                            <label className="container-check mb-0">
                                                <input
                                                    type="checkbox"
                                                    disabled={isView || isSkeletonView}
                                                    checked={allSelected}
                                                    onChange={handleSelectAll}
                                                />
                                                <span className={`checkmark ${isView || isSkeletonView ? "cursor-disabled" : ""}`}></span>
                                                Select All
                                            </label>
                                        </div>
                                    </div>
                                    <div className="d-flex">
                                        <div className="flex-grow-1 mr-2">
                                            <label className="d-block mb-1">Export Template</label>
                                            <ReactSelect
                                                options={
                                                    [
                                                        {
                                                            label: "From Local",
                                                            options: exportTemplateList?.with_client?.map(elem => ({
                                                                value: elem.id,
                                                                label: elem.name
                                                            }))
                                                        },
                                                        {
                                                            label: "From Master",
                                                            options: exportTemplateList?.without_client?.map(elem => ({
                                                                value: elem.id,
                                                                label: elem.name
                                                            }))
                                                        }
                                                    ] || []
                                                }
                                                value={template}
                                                isDisabled={isView || isSkeletonView}
                                                isClearable
                                                classNamePrefix="react-select"
                                                onChange={value => setTemplate(value)}
                                                isSearchable={false}
                                                className={showErrorBorder && !template?.value?.trim()?.length ? "error-border" : ""}
                                                placeholder="Select Template"
                                            />
                                        </div>
                                        <div className="flex-grow-1">
                                            <label className="d-block mb-1">Export Property</label>
                                            <ReactSelect
                                                options={
                                                    [
                                                        {
                                                            label: "From Local",
                                                            options: exportPropertyList?.with_client?.map(elem => ({
                                                                value: elem.id,
                                                                label: elem.name
                                                            }))
                                                        },
                                                        {
                                                            label: "From Master",
                                                            options: exportPropertyList?.without_client?.map(elem => ({
                                                                value: elem.id,
                                                                label: elem.name
                                                            }))
                                                        }
                                                    ] || []
                                                }
                                                value={property}
                                                isDisabled={isView || isSkeletonView}
                                                isClearable
                                                classNamePrefix="react-select"
                                                onChange={value => handleSelectExportProperty(value)}
                                                isSearchable={false}
                                                className={showErrorBorder && !property?.value?.trim()?.length ? "error-border" : ""}
                                                placeholder="Select Property"
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex mt-2">
                                        <div className="flex-grow-1 mr-2">
                                            <div className=" input-txt mt-3">
                                                <div className="img-sq-box seting-type checkbox-container check-field mt-0 mr-2">
                                                    <label className="container-checkbox cursor-hand m-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={tableConfig?.show_config?.show_table_heading}
                                                            onChange={() =>
                                                                setTableConfig(prev => ({
                                                                    ...prev,
                                                                    show_config: {
                                                                        ...prev.show_config,
                                                                        show_table_heading: !prev.show_config.show_table_heading
                                                                    }
                                                                }))
                                                            }
                                                            disabled={isView || isSkeletonView}
                                                        />
                                                        <span className={`checkmark ${isView || isSkeletonView ? "cursor-disabled" : ""}`}></span>
                                                    </label>
                                                    Show Table Heading
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {tableConfig?.show_config?.show_table_heading && (
                                        <div className="flex-grow-1 mr-2 mt-2">
                                            <label className="d-block mb-1">Table Heading</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={tableConfig?.ca_chart_type_table_heading?.label ?? ""}
                                                onChange={e => {
                                                    const newLabel = e.target?.value ?? "";
                                                    setTableConfig(prev => ({
                                                        ...prev,
                                                        ca_chart_type_table_heading: {
                                                            ...prev.ca_chart_type_table_heading,
                                                            label: newLabel
                                                        }
                                                    }));
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                                {isLoading ? (
                                    <div className="col-md-12 p-5 box-otr box-wide" style={{ height: 450 }}>
                                        <Loader />
                                    </div>
                                ) : (
                                    <DragDropContext onDragEnd={onDragEnd}>
                                        <Droppable droppableId="all-categories" type="CATEGORY">
                                            {provided => (
                                                <div
                                                    className="ca-bands-container"
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    style={{ minHeight: 350, maxHeight: 350, overflowY: "auto", ...provided.droppableProps.style }}
                                                >
                                                    {filteredBands.map((band, bandIndex) => (
                                                        <Draggable key={band.key} draggableId={band.key} index={bandIndex}>
                                                            {provided => (
                                                                <div ref={provided.innerRef} {...provided.draggableProps}>
                                                                    <div className="card p-0 mb-2">
                                                                        <div
                                                                            className="card-header"
                                                                            id={`heading-${band.key}`}
                                                                            {...provided.dragHandleProps}
                                                                        >
                                                                            <div
                                                                                className="d-flex content-block-2 otr-recom-div no-bottom-bord btn"
                                                                                data-toggle="collapse"
                                                                                data-target={`#collapse-${band.key}`}
                                                                                aria-expanded="false"
                                                                                aria-controls={`collapse-${band.key}`}
                                                                            >
                                                                                <div className="image-drag mr-2">
                                                                                    <img src="/img/Group 2.svg" alt="" />
                                                                                </div>
                                                                                <button
                                                                                    className="btn heading-btn-accordion"
                                                                                    data-toggle="collapse"
                                                                                    data-target={`#collapse-${band.key}`}
                                                                                    aria-expanded="false"
                                                                                    aria-controls={`collapse-${band.key}`}
                                                                                >
                                                                                    {band.label}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            id={`collapse-${band.key}`}
                                                                            className="collapse"
                                                                            aria-labelledby={`heading-${band.key}`}
                                                                            data-parent="#accordion"
                                                                        >
                                                                            <Droppable
                                                                                droppableId={`category-${bandIndex}`}
                                                                                type={`group-${bandIndex}`}
                                                                            >
                                                                                {provided => (
                                                                                    <div
                                                                                        ref={provided.innerRef}
                                                                                        className="col-md-12 check-otr d-flex checkbox-sec height-redu"
                                                                                        {...provided.droppableProps}
                                                                                    >
                                                                                        <div className="col-md-12 box-otr box-wide total-summary">
                                                                                            <div className="rem-txt">
                                                                                                <label className="container-check">
                                                                                                    Select All (
                                                                                                    {band.items.filter(item => item.selected).length}{" "}
                                                                                                    / {band.items.length})
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        disabled={isView || isSkeletonView}
                                                                                                        checked={band.items
                                                                                                            .filter(item =>
                                                                                                                item.name
                                                                                                                    ?.toLowerCase()
                                                                                                                    .includes(search?.toLowerCase())
                                                                                                            )
                                                                                                            .every(item => item.selected)}
                                                                                                        onChange={() => handleBandSelectAll(band.key)}
                                                                                                    />
                                                                                                    <span
                                                                                                        className={`checkmark ${
                                                                                                            isView || isSkeletonView
                                                                                                                ? "cursor-disabled"
                                                                                                                : ""
                                                                                                        }`}
                                                                                                    ></span>
                                                                                                </label>
                                                                                            </div>
                                                                                        </div>
                                                                                        {band.items.map((item, itemIndex) => (
                                                                                            <Draggable
                                                                                                key={item.id}
                                                                                                draggableId={`${band.key}-${item.id}`}
                                                                                                index={itemIndex}
                                                                                            >
                                                                                                {provided => (
                                                                                                    <div
                                                                                                        ref={provided.innerRef}
                                                                                                        {...provided.draggableProps}
                                                                                                        {...provided.dragHandleProps}
                                                                                                        className="draggable-item"
                                                                                                        onMouseEnter={() => setHoveredItemId(item.id)}
                                                                                                        onMouseLeave={() => setHoveredItemId(null)}
                                                                                                    >
                                                                                                        <div
                                                                                                            className={`col-md-12 box-otr box-wide ${
                                                                                                                expandedFields.includes(item.id)
                                                                                                                    ? "box-expanded"
                                                                                                                    : ""
                                                                                                            }`}
                                                                                                        >
                                                                                                            <EditableName
                                                                                                                value={item.name}
                                                                                                                originalValue={item.originalLabel}
                                                                                                                disabled={isView || isSkeletonView}
                                                                                                                checked={item.selected}
                                                                                                                onCheck={() =>
                                                                                                                    handleItemCheck(band.key, item.id)
                                                                                                                }
                                                                                                                onSave={newName => {
                                                                                                                    const updated = [...bands];
                                                                                                                    updated[bandIndex].items[
                                                                                                                        itemIndex
                                                                                                                    ].name = newName;
                                                                                                                    setBands(updated);
                                                                                                                }}
                                                                                                                isHovered={hoveredItemId === item.id}
                                                                                                            />

                                                                                                            <div className="right-box-content d-flex align-items-center justify-content-between">
                                                                                                                <div className="input-box flex-grow-1">
                                                                                                                    <input
                                                                                                                        autoComplete="nope"
                                                                                                                        className={`custom-input form-control ${
                                                                                                                            isView || isSkeletonView
                                                                                                                                ? "cursor-disabled"
                                                                                                                                : ""
                                                                                                                        }`}
                                                                                                                        type="text"
                                                                                                                        value={item.width}
                                                                                                                        inputMode="numeric"
                                                                                                                        disabled={
                                                                                                                            isView || isSkeletonView
                                                                                                                        }
                                                                                                                        onChange={e => {
                                                                                                                            const updated = [
                                                                                                                                ...bands
                                                                                                                            ];
                                                                                                                            updated[bandIndex].items[
                                                                                                                                itemIndex
                                                                                                                            ].width = e.target.value;
                                                                                                                            setBands(updated);
                                                                                                                        }}
                                                                                                                    />
                                                                                                                </div>

                                                                                                                <button
                                                                                                                    className="btn btn-link p-0 ml-2"
                                                                                                                    type="button"
                                                                                                                    onClick={() =>
                                                                                                                        toggleCollapse(item.id)
                                                                                                                    }
                                                                                                                    aria-expanded={expandedFields.includes(
                                                                                                                        item.id
                                                                                                                    )}
                                                                                                                    aria-controls={`collapse-${item.id}`}
                                                                                                                >
                                                                                                                    <i
                                                                                                                        className={`fas fa-chevron-${
                                                                                                                            expandedFields.includes(
                                                                                                                                item.id
                                                                                                                            )
                                                                                                                                ? "up"
                                                                                                                                : "down"
                                                                                                                        }`}
                                                                                                                    />
                                                                                                                </button>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div
                                                                                                            className={`col-md-12 collapse ${
                                                                                                                expandedFields.includes(item.id)
                                                                                                                    ? "show"
                                                                                                                    : ""
                                                                                                            }`}
                                                                                                            id={`collapse-${item.id}`}
                                                                                                        >
                                                                                                            <div className="row py-2 ca-config-alignment-wrapper">
                                                                                                                <div className="col-md-6">
                                                                                                                    <label>Heading Alignment</label>
                                                                                                                    <select
                                                                                                                        value={
                                                                                                                            item.heading_alignment ||
                                                                                                                            "left"
                                                                                                                        }
                                                                                                                        onChange={e =>
                                                                                                                            handleAlignmentChange(
                                                                                                                                "heading_alignment",
                                                                                                                                e.target.value,
                                                                                                                                bandIndex,
                                                                                                                                itemIndex
                                                                                                                            )
                                                                                                                        }
                                                                                                                        disabled={
                                                                                                                            isView || isSkeletonView
                                                                                                                        }
                                                                                                                        className="form-control"
                                                                                                                    >
                                                                                                                        <option value="left">
                                                                                                                            Left
                                                                                                                        </option>
                                                                                                                        <option value="center">
                                                                                                                            Center
                                                                                                                        </option>
                                                                                                                        <option value="right">
                                                                                                                            Right
                                                                                                                        </option>
                                                                                                                    </select>
                                                                                                                </div>
                                                                                                                <div className="col-md-6">
                                                                                                                    <label>Body Alignment</label>
                                                                                                                    <select
                                                                                                                        value={
                                                                                                                            item.body_alignment ||
                                                                                                                            "left"
                                                                                                                        }
                                                                                                                        onChange={e =>
                                                                                                                            handleAlignmentChange(
                                                                                                                                "body_alignment",
                                                                                                                                e.target.value,
                                                                                                                                bandIndex,
                                                                                                                                itemIndex
                                                                                                                            )
                                                                                                                        }
                                                                                                                        disabled={
                                                                                                                            isView || isSkeletonView
                                                                                                                        }
                                                                                                                        className="form-control"
                                                                                                                    >
                                                                                                                        <option value="left">
                                                                                                                            Left
                                                                                                                        </option>
                                                                                                                        <option value="center">
                                                                                                                            Center
                                                                                                                        </option>
                                                                                                                        <option value="right">
                                                                                                                            Right
                                                                                                                        </option>
                                                                                                                    </select>
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )}
                                                                                            </Draggable>
                                                                                        ))}
                                                                                        {provided.placeholder}
                                                                                        {!band.config?.is_static && (
                                                                                            <div className="highlight-wrapper-out">
                                                                                                <div
                                                                                                    className={`col-md-12 box-otr box-wide total-summary mr-2 ${
                                                                                                        expandedFields.includes(`${band.key}-total`)
                                                                                                            ? "box-expanded"
                                                                                                            : ""
                                                                                                    }`}
                                                                                                    onMouseEnter={() =>
                                                                                                        setHoveredItemId(`${band.key}-total`)
                                                                                                    }
                                                                                                    onMouseLeave={() => setHoveredItemId(null)}
                                                                                                >
                                                                                                    <EditableName
                                                                                                        value={band.config?.displayTotalLabel}
                                                                                                        originalValue={
                                                                                                            band.config?.originalTotalLabel
                                                                                                        }
                                                                                                        disabled={isView || isSkeletonView}
                                                                                                        checked={band.config?.show_total}
                                                                                                        onCheck={e => {
                                                                                                            const updatedBands = [...bands];
                                                                                                            updatedBands[bandIndex].config = {
                                                                                                                ...updatedBands[bandIndex].config,
                                                                                                                show_total: e.target.checked
                                                                                                            };
                                                                                                            setBands(updatedBands);
                                                                                                        }}
                                                                                                        onSave={newName => {
                                                                                                            const updatedBands = [...bands];
                                                                                                            updatedBands[bandIndex].config = {
                                                                                                                ...updatedBands[bandIndex].config,
                                                                                                                displayTotalLabel: newName
                                                                                                            };
                                                                                                            setBands(updatedBands);
                                                                                                        }}
                                                                                                        isHovered={
                                                                                                            hoveredItemId === `${band.key}-total`
                                                                                                        }
                                                                                                    />

                                                                                                    <div className="right-box-content d-flex align-items-center justify-content-between">
                                                                                                        <div className="input-box flex-grow-1">
                                                                                                            <input
                                                                                                                autoComplete="nope"
                                                                                                                className={`custom-input form-control ${
                                                                                                                    isView || isSkeletonView
                                                                                                                        ? "cursor-disabled"
                                                                                                                        : ""
                                                                                                                }`}
                                                                                                                type="text"
                                                                                                                value={band.config?.width}
                                                                                                                inputMode="numeric"
                                                                                                                disabled={isView || isSkeletonView}
                                                                                                                onChange={e => {
                                                                                                                    const updatedBands = [...bands];
                                                                                                                    updatedBands[bandIndex].config = {
                                                                                                                        ...updatedBands[bandIndex]
                                                                                                                            .config,
                                                                                                                        width: e.target.value
                                                                                                                    };
                                                                                                                    setBands(updatedBands);
                                                                                                                }}
                                                                                                            />
                                                                                                        </div>

                                                                                                        {/* Toggle button for heading alignment */}
                                                                                                        <button
                                                                                                            className="btn btn-link p-0 ml-2"
                                                                                                            type="button"
                                                                                                            onClick={() =>
                                                                                                                toggleCollapse(`${band.key}-total`)
                                                                                                            }
                                                                                                            aria-expanded={expandedFields.includes(
                                                                                                                `${band.key}-total`
                                                                                                            )}
                                                                                                            aria-controls={`collapse-${band.key}-total`}
                                                                                                            disabled={isView || isSkeletonView}
                                                                                                        >
                                                                                                            <i
                                                                                                                className={`fas fa-chevron-${
                                                                                                                    expandedFields.includes(
                                                                                                                        `${band.key}-total`
                                                                                                                    )
                                                                                                                        ? "up"
                                                                                                                        : "down"
                                                                                                                }`}
                                                                                                            />
                                                                                                        </button>
                                                                                                    </div>

                                                                                                    {/* Collapsible heading alignment select */}
                                                                                                </div>
                                                                                                <div
                                                                                                    className={`col-md-12 collapse ${
                                                                                                        expandedFields.includes(`${band.key}-total`)
                                                                                                            ? "show"
                                                                                                            : ""
                                                                                                    }`}
                                                                                                    id={`collapse-${band.key}-total`}
                                                                                                >
                                                                                                    <div className="row py-2 ca-config-alignment-wrapper">
                                                                                                        <div className="col-md-6">
                                                                                                            <label>Heading Alignment</label>
                                                                                                            <select
                                                                                                                value={
                                                                                                                    band.config?.heading_alignment ||
                                                                                                                    "left"
                                                                                                                }
                                                                                                                onChange={e => {
                                                                                                                    const updatedBands = [...bands];
                                                                                                                    updatedBands[bandIndex].config = {
                                                                                                                        ...updatedBands[bandIndex]
                                                                                                                            .config,
                                                                                                                        heading_alignment:
                                                                                                                            e.target.value
                                                                                                                    };
                                                                                                                    setBands(updatedBands);
                                                                                                                }}
                                                                                                                disabled={isView || isSkeletonView}
                                                                                                                className="form-control"
                                                                                                            >
                                                                                                                <option value="left">Left</option>
                                                                                                                <option value="center">Center</option>
                                                                                                                <option value="right">Right</option>
                                                                                                            </select>
                                                                                                        </div>
                                                                                                        <div className="col-md-6">
                                                                                                            <label>Body Alignment</label>
                                                                                                            <select
                                                                                                                value={
                                                                                                                    band.config?.body_alignment ||
                                                                                                                    "left"
                                                                                                                }
                                                                                                                onChange={e => {
                                                                                                                    const updatedBands = [...bands];
                                                                                                                    updatedBands[bandIndex].config = {
                                                                                                                        ...updatedBands[bandIndex]
                                                                                                                            .config,
                                                                                                                        body_alignment: e.target.value
                                                                                                                    };
                                                                                                                    setBands(updatedBands);
                                                                                                                }}
                                                                                                                disabled={isView || isSkeletonView}
                                                                                                                className="form-control"
                                                                                                            >
                                                                                                                <option value="left">Left</option>
                                                                                                                <option value="center">Center</option>
                                                                                                                <option value="right">Right</option>
                                                                                                            </select>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </Droppable>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                                <div className="col-md-12 p-0 mt-4 text-right btnOtr d-flex">
                                    <div className="tot-btr">
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="btn btn-secondary btnClr col-md-3 mr-1"
                                            data-dismiss="modal"
                                        >
                                            Cancel
                                        </button>
                                        {props.hasStepper && props.onBack && (
                                            <button type="button" onClick={props.onBack} className="btn btn-secondary btnClr col-md-3 mr-1">
                                                Back
                                            </button>
                                        )}
                                        {!isView && !isSkeletonView && (
                                            <button type="button" onClick={handleSave} className="btn btn-primary btnRgion btn-wid">
                                                Ok
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </OuterDraggable>
                </div>
                {renderConfirmationModal()}
            </div>
        </React.Fragment>
    );
};

function EditableName({ value, originalValue, disabled, checked, onCheck, onSave, isHovered }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [error, setError] = useState("");
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

    useEffect(() => {
        if (!editing) {
            setDraft(value);
            setError("");
        }
    }, [value, editing]);

    const startEdit = e => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setEditing(true);
    };

    const confirmEdit = () => {
        if (draft.trim() === "") {
            setError("Name cannot be empty");
            return;
        }
        onSave(draft.trim());
        setEditing(false);
        setError("");
    };

    const cancelEdit = () => {
        setEditing(false);
        setDraft(value);
        setError("");
    };

    const handleRestoreClick = e => {
        e.preventDefault();
        e.stopPropagation();
        setShowRestoreConfirm(true);
    };

    const handleConfirmRestore = () => {
        onSave(originalValue);
        setShowRestoreConfirm(false);
    };

    const handleCancelRestore = () => {
        setShowRestoreConfirm(false);
    };

    return (
        <>
            <div className="rem-txt">
                {editing ? (
                    <div className="d-flex align-items-center">
                        <input
                            type="text"
                            className={`form-control ${error ? "is-invalid" : ""}`}
                            value={draft}
                            autoFocus
                            onChange={e => {
                                setDraft(e.target.value);
                                if (e.target.value.trim() !== "") setError("");
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter") confirmEdit();
                                if (e.key === "Escape") cancelEdit();
                            }}
                            onBlur={confirmEdit}
                        />
                        {/* <i
                            className={`fas fa-check ml-2 ${draft.trim() === "" ? "text-muted disabled cursor-disabled" : "cursor-hand"}`}
                            role="button"
                            title="Save"
                            onClick={confirmEdit}
                        />
                        <i
                            className="fas fa-times ml-2 cursor-hand"
                            role="button"
                            title="Cancel"
                            onClick={e => {
                                e.stopPropagation();
                                cancelEdit();
                            }}
                        /> */}
                    </div>
                ) : (
                    <label className="container-check">
                        {value}
                        <input type="checkbox" disabled={disabled} checked={checked} onChange={onCheck} />
                        <span className={`checkmark ${disabled ? "cursor-disabled" : ""}`} />
                        {!disabled && (
                            <>
                                <i className="fas fa-pencil-alt ml-4 edit-icon" role="button" title="Rename" onClick={startEdit} />
                                {value !== originalValue && (
                                    <i
                                        className="fas fa-undo ml-4 restore-icon"
                                        role="button"
                                        title="Restore original"
                                        onClick={handleRestoreClick}
                                    />
                                )}
                            </>
                        )}
                    </label>
                )}

                {error && <small className="text-danger d-block mt-1">{error}</small>}
            </div>

            {showRestoreConfirm && (
                <Portal
                    body={
                        <ConfirmationModal
                            heading={`Restore “${value}” to “${originalValue}”?`}
                            message="This will discard your custom name and revert to the original."
                            onYes={handleConfirmRestore}
                            onNo={handleCancelRestore}
                            type="cancel"
                        />
                    }
                    onCancel={handleCancelRestore}
                />
            )}
        </>
    );
}
